/* Native Gardeners accessibility enhancements — added 2026-08-31, revised 2026-09-01, 09-02, 09-09
   Purpose (WCAG 2.1 AA alignment):
   1) Screen-reader announcements confirming cart additions/removals (WCAG 4.1.3 Status Messages)
   2) Current cart-item count exposed in the cart control's accessible name (WCAG 4.1.2 Name, Role, Value)
   3) Escape key closes the cart drawer (expected dialog behavior)
   4) Dialog semantics on the cart drawer as a runtime safety net
   5) Focus returns to the triggering control when the drawer closes (WCAG 2.4.3 Focus Order)
   6) Keyboard focus is kept inside the drawer while it is open, including across the theme's asynchronous cart re-render (WCAG 2.4.3)
   7) Tab/Shift+Tab move through every control in the drawer and wrap around; works around the theme's focus trap, which pins focus on the Close button when the first item is added to an empty cart (WCAG 2.1.1/2.1.2)
   Added 2026-09-09 (round 2):
   8) Header drop-down menus: the expanded/collapsed state is reported on the menu control itself and Escape closes an open menu (WCAG 4.1.2)
   9) Search suggestions: the number of suggestions is announced when they appear, and the search box reports whether its suggestion list is open (WCAG 4.1.3, 4.1.2)
   10) Pop-up dialogs (newsletter sign-up, quick shop, video): dialog role and accessible name, and focus goes back to where it was when the dialog closes (WCAG 4.1.2, 2.4.3)
   11) Pagination: navigation landmark, current page marked, page-number links named "Page N" (WCAG 1.3.1, 2.4.4)
   Version 3 was applied to the theme copy "ADA round 2 - preview then publish (Sept 9)" on 2026-09-09.
   Added 2026-09-18 (round 3, after an automated scan of the live site):
   12) Icon-only buttons get an accessible name: product-image zoom, thumbnail arrows, search close, and any other button that contains only an icon (WCAG 4.1.2)
   13) Embedded frames without a title get one, including Shopify's analytics sandbox frame (WCAG 4.1.2)
   14) Page sections that sit outside the header, main and footer landmarks (announcement bar, notices, footer promotions) are exposed as named regions (WCAG 1.3.1 best practice)
   15) Section titles marked up as h3 directly under the page's h1 are announced at level 2, and images inside links that repeat the link text are marked decorative (best practice)
   16) The wishlist app's hidden "Login required" dialog is hidden from assistive technology while closed and gets dialog semantics when open (WCAG 1.3.2, 4.1.2)
   17) The app-injected low-stock notice is darkened from 2.85:1 to about 6.5:1 contrast (WCAG 1.4.3)
   Version 4 — applied to the theme copy "ADA round 3 (Sept 18) - preview then publish" on 2026-09-18.
*/
(function () {
  'use strict';

  /* --- 1) Polite live region announcing cart changes --- */
  var live = document.getElementById('a11y-cart-status');
  if (!live) {
    live = document.createElement('div');
    live.id = 'a11y-cart-status';
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    live.style.cssText = 'position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;';
    document.body.appendChild(live);
  }

  var lastCount = null;

  function itemsText(count) {
    return count + (count === 1 ? ' item' : ' items');
  }

  /* --- 2) Expose the current count on the cart control(s) --- */
  function updateCartControls(count) {
    var controls = document.querySelectorAll('a.js-drawer-open-cart, a[href="/cart"]');
    for (var i = 0; i < controls.length; i++) {
      var a = controls[i];
      var fallback = a.querySelector('.icon__fallback-text');
      var base = (fallback && fallback.textContent ? fallback.textContent : 'Cart').replace(/\s+/g, ' ').trim() || 'Cart';
      a.setAttribute('aria-label', base + ', ' + itemsText(count));
    }
  }

  function announce(count, totalCents) {
    if (count === lastCount) return;
    var msg;
    if (lastCount !== null && count > lastCount) msg = 'Item added to cart. ';
    else if (lastCount !== null && count < lastCount) msg = 'Item removed from cart. ';
    else msg = '';
    msg += 'Cart has ' + itemsText(count);
    if (typeof totalCents === 'number') msg += ', total $' + (totalCents / 100).toFixed(2);
    lastCount = count;
    live.textContent = '';
    window.setTimeout(function () { live.textContent = msg + '.'; }, 50);
  }

  function refreshFromCart() {
    try {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          updateCartControls(cart.item_count);
          announce(cart.item_count, cart.total_price);
        })
        .catch(function () {});
    } catch (e) { /* no-op */ }
  }

  /* Initialize: label the control with the current count, silently */
  try {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        lastCount = cart.item_count;
        updateCartControls(cart.item_count);
      })
      .catch(function () {});
  } catch (e) {}

  /* Motion updates the header cart bubble on every cart change — observe it */
  var bubble = document.querySelector('.cart-link__bubble');
  if (bubble && window.MutationObserver) {
    new MutationObserver(refreshFromCart)
      .observe(bubble, { childList: true, characterData: true, subtree: true, attributes: true });
  }
  /* Archetype themes also publish this event where available */
  document.addEventListener('cart:updated', refreshFromCart);

  /* --- 3 & 4) Cart drawer: dialog semantics + Escape closes --- */
  var drawer = document.getElementById('CartDrawer');
  if (drawer) {
    if (!drawer.getAttribute('role')) drawer.setAttribute('role', 'dialog');
    if (!drawer.getAttribute('aria-modal')) drawer.setAttribute('aria-modal', 'true');
    if (!drawer.getAttribute('aria-label')) drawer.setAttribute('aria-label', 'Shopping cart');
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      if (!drawer.classList.contains('drawer--is-open')) return;
      var closeBtn = drawer.querySelector('.js-drawer-close');
      if (closeBtn) closeBtn.click();
    });

    /* --- 5) Return focus to the triggering control when the drawer closes (WCAG 2.4.3 Focus Order).
       The theme restores focus only when the drawer was opened from a cart icon; when it opens
       automatically after "Add to cart" (or is closed with Escape), focus would otherwise land on <body>.
       We remember the last focusable element used outside the drawer and restore it if focus is lost. --- */
    var lastOutside = null;
    function remember(el) {
      if (drawer.classList.contains('drawer--is-open')) return; /* focus that strays outside an open drawer is not the trigger */
      if (el && el !== document.body && el.nodeType === 1 && !drawer.contains(el)) lastOutside = el;
    }
    document.addEventListener('focusin', function (e) { remember(e.target); }, true);
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('button, a, [tabindex]') : null;
      remember(t);
    }, true);

    function restoreIfLost() {
      var ae = document.activeElement;
      var lost = !ae || ae === document.body || drawer.contains(ae);
      if (lost && lastOutside && document.contains(lastOutside) && !drawer.classList.contains('drawer--is-open')) {
        try { lastOutside.focus({ preventScroll: true }); } catch (err) { try { lastOutside.focus(); } catch (e2) {} }
      }
    }
    /* --- 6) Guarantee keyboard focus is inside the drawer while it is open (WCAG 2.4.3).
       The theme focuses the drawer container when it opens, but it also re-renders the cart
       contents asynchronously right after "Add to cart"; if focus is on a control that gets
       replaced, or the container focus did not take, Tab would walk the page behind the drawer.
       We move focus to the drawer container whenever the drawer is open and focus is not inside it. --- */
    var lastInDrawer = null; /* descriptor of the last control focused inside the drawer */
    document.addEventListener('focusin', function (e) {
      var t = e.target;
      if (!t || t === drawer || !drawer.contains(t)) return;
      lastInDrawer = { tag: t.tagName, label: t.getAttribute('aria-label'), name: t.getAttribute('name'), cls: t.className && String(t.className).split(/\s+/)[0] };
    }, true);
    function findInDrawer(d) {
      if (!d) return null;
      var cands = drawer.querySelectorAll(d.tag);
      for (var i = 0; i < cands.length; i++) {
        var c = cands[i];
        if (c.offsetParent === null) continue;
        if (d.label && c.getAttribute('aria-label') === d.label) return c;
        if (d.name && c.getAttribute('name') === d.name) return c;
        if (!d.label && !d.name && d.cls && c.className && String(c.className).split(/\s+/)[0] === d.cls) return c;
      }
      return null;
    }
    function focusIntoDrawer() {
      if (!drawer.classList.contains('drawer--is-open')) return;
      var ae = document.activeElement;
      if (ae && ae !== document.body && drawer.contains(ae)) return;
      var target = findInDrawer(lastInDrawer) || drawer;
      if (target === drawer && !drawer.hasAttribute('tabindex')) drawer.setAttribute('tabindex', '-1');
      try { target.focus({ preventScroll: true }); } catch (err) { try { target.focus(); } catch (e2) {} }
    }

    if (window.MutationObserver) {
      var wasOpen = drawer.classList.contains('drawer--is-open');
      new MutationObserver(function () {
        var open = drawer.classList.contains('drawer--is-open');
        if (!wasOpen && open) {
          lastInDrawer = null; /* a fresh open starts at the drawer container, not at last session's control */
          window.setTimeout(focusIntoDrawer, 150);
          window.setTimeout(focusIntoDrawer, 800); /* after the async cart re-render */
        }
        if (wasOpen && !open) {
          window.setTimeout(restoreIfLost, 60);
          window.setTimeout(restoreIfLost, 600); /* after the slide-out transition hides the drawer */
        }
        wasOpen = open;
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });

      /* If the cart contents are re-rendered while the drawer is open and focus was lost, put it back */
      new MutationObserver(function () {
        if (drawer.classList.contains('drawer--is-open')) window.setTimeout(focusIntoDrawer, 30);
      }).observe(drawer, { childList: true, subtree: true });
    }

    /* --- 7) Tab key inside the drawer (WCAG 2.1.1 / 2.1.2). The theme's own focus trap computes the
       first and last focusable controls at the moment the drawer opens. When the first item is added to
       an EMPTY cart, the cart contents are still hidden at that moment, so the Close button is both
       "first" and "last"; the theme's keydown handler then cancels every Tab press on Close and re-focuses
       Close, pinning keyboard focus there (reproduced Sept 9, 2026). Its namespaced handlers also cannot be
       removed once registered. We stop the Tab keydown from reaching that handler and do the wrap-around
       ourselves, from a fresh list of the drawer's controls on every key press. --- */
    function drawerFocusables() {
      var els = drawer.querySelectorAll('button, [href], input, select, textarea, iframe, [tabindex]:not([tabindex^="-"])');
      return Array.prototype.filter.call(els, function (el) {
        return el.offsetParent !== null && !el.disabled && el.getAttribute('aria-hidden') !== 'true' && el !== drawer;
      });
    }
    var tabAt = 0, tabShift = false;
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' && e.keyCode !== 9) return;
      if (!drawer.classList.contains('drawer--is-open')) return;
      e.stopPropagation(); /* keeps the theme's stale trap handler from cancelling the key */
      tabAt = Date.now(); tabShift = !!e.shiftKey;
      var list = drawerFocusables();
      if (!list.length) return;
      var ae = document.activeElement;
      if (!ae || ae === document.body || !drawer.contains(ae) || ae === drawer) {
        e.preventDefault();
        (tabShift ? list[list.length - 1] : list[0]).focus();
        return;
      }
      if (!tabShift && ae === list[list.length - 1] && ae.tagName !== 'IFRAME') { e.preventDefault(); list[0].focus(); return; }
      if (tabShift && ae === list[0]) { e.preventDefault(); list[list.length - 1].focus(); return; }
      /* otherwise the browser's normal sequential focus moves to the next control in the drawer */
    }, true);
    /* If a keyboard Tab carried focus out of the open drawer (e.g. past an embedded payment button), bring it back */
    document.addEventListener('focusin', function (e) {
      if (!drawer.classList.contains('drawer--is-open')) return;
      if (Date.now() - tabAt > 300) return; /* only keyboard-driven focus moves, never clicks or app pop-ups */
      var t = e.target;
      if (!t || drawer.contains(t)) return;
      var list = drawerFocusables();
      if (list.length) (tabShift ? list[list.length - 1] : list[0]).focus();
    }, true);
  }

  /* ---------- Round 2 additions (2026-09-09) ---------- */

  /* Shared polite status region for non-cart announcements */
  var status = document.getElementById('a11y-status');
  if (!status) {
    status = document.createElement('div');
    status.id = 'a11y-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.style.cssText = 'position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0;';
    document.body.appendChild(status);
  }
  function say(msg) {
    status.textContent = '';
    window.setTimeout(function () { status.textContent = msg; }, 50);
  }

  /* --- 8) Header drop-down menus (WCAG 4.1.2). The theme builds each top-level menu item with a
     drop-down as <details><summary>; it opens the menu on hover and on keyboard focus, but it records the
     expanded state on the <details> element, while the <summary> the user is focused on always says
     aria-expanded="false". Keep the summary's state in sync with the menu, and let Escape close an open
     menu and return focus to its control. --- */
  var navDetails = document.querySelectorAll('details.site-nav__details');
  Array.prototype.forEach.call(navDetails, function (det) {
    var summary = det.querySelector('summary');
    if (!summary) return;
    function sync() { summary.setAttribute('aria-expanded', det.hasAttribute('open') ? 'true' : 'false'); }
    sync();
    if (window.MutationObserver) new MutationObserver(sync).observe(det, { attributes: true, attributeFilter: ['open'] });
    det.addEventListener('toggle', sync);
    det.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') return;
      if (!det.hasAttribute('open')) return;
      e.stopPropagation();
      try { summary.focus(); } catch (err) {}          /* focus first, so the theme's focus-in handler does not reopen it */
      det.removeAttribute('open');
      det.setAttribute('aria-expanded', 'false');
      sync();
    });
  });

  /* --- 9) Search suggestions (WCAG 4.1.3 Status Messages). The theme renders suggestions into
     #predictive-search as the user types, but nothing tells a screen-reader user that they appeared,
     and the search box's aria-expanded never changes. Announce a short summary of what appeared and
     keep the combobox state current. --- */
  Array.prototype.forEach.call(document.querySelectorAll('predictive-search'), function (ps) {
    var input = ps.querySelector('input[type="search"]');
    var results = ps.querySelector('#predictive-search');
    if (!input || !results) return;
    var lastMsg = null, timer = null;
    function isOpen() { return results.style.display !== 'none' && results.style.display !== '' && results.querySelector('#predictive-search-results'); }
    function describe() {
      var groups = results.querySelectorAll('.results');
      var parts = [];
      Array.prototype.forEach.call(groups, function (g) {
        var n = g.querySelectorAll('li[role="option"], li').length;
        if (!n) return;
        var h = g.querySelector('h1, h2, h3, h4, h5, h6');
        var label = h ? h.textContent.replace(/\s+/g, ' ').trim().toLowerCase() : 'results';
        if (n === 1 && /s$/.test(label)) label = label.replace(/s$/, '');
        parts.push(n + ' ' + label);
      });
      if (!parts.length) return 'No search suggestions.';
      return 'Search suggestions: ' + parts.join(', ') + '. Press Tab to review them.';
    }
    function update() {
      var open = !!isOpen();
      input.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (!open) { lastMsg = null; return; }
      var msg = describe();
      if (msg !== lastMsg) { lastMsg = msg; say(msg); }
    }
    function schedule() { window.clearTimeout(timer); timer = window.setTimeout(update, 200); }
    if (window.MutationObserver) {
      new MutationObserver(schedule).observe(results, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    }
  });

  /* --- 10) Pop-up dialogs (WCAG 4.1.2, 2.4.3). The theme's modals (newsletter sign-up, quick shop,
     video) carry no dialog role or name, and when one closes without having been opened from a button,
     focus is dropped on the page body. Give each modal dialog semantics when it opens and return focus
     to the element that had it before the dialog appeared. --- */
  Array.prototype.forEach.call(document.querySelectorAll('.modal'), function (modal) {
    var before = null;
    function label(final) {
      if (modal.getAttribute('aria-labelledby') || modal.getAttribute('aria-label')) return;
      var h = modal.querySelector('h1, h2, h3, .h1, .h2, .h3, .product-single__title');
      if (h && h.textContent.replace(/\s+/g, ' ').trim()) {
        if (!h.id) h.id = (modal.id || 'modal') + '-title';
        modal.setAttribute('aria-labelledby', h.id);
      } else if (final) {
        var name = /quick-shop/.test(modal.className) ? 'Quick shop' : (/video/i.test(modal.id) ? 'Video' : 'Dialog');
        modal.setAttribute('aria-label', name);
      }
    }
    function onOpen() {
      if (!modal.getAttribute('role')) modal.setAttribute('role', 'dialog');
      if (!modal.getAttribute('aria-modal')) modal.setAttribute('aria-modal', 'true');
      var ae = document.activeElement;
      before = (ae && ae !== document.body && !modal.contains(ae)) ? ae : null;
      label(false);
      window.setTimeout(function () { label(false); }, 400);  /* quick-shop content is loaded after the modal opens */
      window.setTimeout(function () { label(true); }, 1500);
    }
    function onClose() {
      window.setTimeout(function () {
        var ae = document.activeElement;
        if ((!ae || ae === document.body) && before && document.contains(before)) {
          try { before.focus({ preventScroll: true }); } catch (err) { try { before.focus(); } catch (e2) {} }
        }
        before = null;
      }, 600); /* after the theme's own 500 ms close routine, which restores focus only for button-opened modals */
    }
    if (window.MutationObserver) {
      var active = modal.classList.contains('modal--is-active');
      new MutationObserver(function () {
        var now = modal.classList.contains('modal--is-active');
        if (!active && now) onOpen();
        if (active && !now) onClose();
        active = now;
      }).observe(modal, { attributes: true, attributeFilter: ['class'] });
    }
  });

  /* --- 11) Pagination (WCAG 1.3.1, 2.4.4). Mark the pagination block as a navigation landmark, flag the
     current page, and give the bare page-number links a name that says what they are. --- */
  Array.prototype.forEach.call(document.querySelectorAll('.pagination'), function (pag) {
    if (pag.tagName !== 'NAV' && !pag.getAttribute('role')) {
      pag.setAttribute('role', 'navigation');
      if (!pag.getAttribute('aria-label')) pag.setAttribute('aria-label', 'Pagination');
    }
    var cur = pag.querySelector('.current');
    if (cur && !cur.getAttribute('aria-current')) cur.setAttribute('aria-current', 'page');
    Array.prototype.forEach.call(pag.querySelectorAll('a'), function (a) {
      var t = a.textContent.replace(/\s+/g, ' ').trim();
      if (/^\d+$/.test(t) && !a.getAttribute('aria-label')) a.setAttribute('aria-label', 'Page ' + t);
    });
  });

  /* ---------- Round 3 additions (2026-09-18) ---------- */

  /* --- 12) Icon-only buttons (WCAG 4.1.2 Name, Role, Value). The theme's product-image zoom button and
     thumbnail arrows, and the search-close button, contain only an aria-hidden SVG, so a screen reader
     announces "button" with no name. Name the known ones, then catch any other icon-only control by its
     icon class. Re-run when content is added dynamically (quick shop, cart re-render). --- */
  var knownIcons = [
    ['.js-photoswipe__zoom', 'Zoom image'],
    ['.product__thumb-arrow--prev', 'Previous image'],
    ['.product__thumb-arrow--next', 'Next image'],
    ['.btn--close-search', 'Close search'],
    ['.js-drawer-close', 'Close']
  ];
  var iconWords = { 'icon-search': 'Search', 'icon-close': 'Close', 'icon-chevron-left': 'Previous', 'icon-chevron-right': 'Next', 'icon-arrow-left': 'Previous', 'icon-arrow-right': 'Next', 'icon-chevron-down': 'Open', 'icon-plus': 'Increase', 'icon-minus': 'Decrease', 'icon-play': 'Play', 'icon-filter': 'Filter', 'icon-hamburger': 'Menu', 'icon-bag-minimal': 'Cart', 'icon-cart': 'Cart', 'icon-user': 'Account', 'icon-zoom': 'Zoom' };
  function ownText(el) {
    var clone = el.cloneNode(true);
    Array.prototype.forEach.call(clone.querySelectorAll('svg, [aria-hidden="true"]'), function (n) { n.parentNode && n.parentNode.removeChild(n); });
    return clone.textContent.replace(/\s+/g, '');
  }
  function hasName(el) {
    return !!(el.getAttribute('aria-label') || el.getAttribute('aria-labelledby') || el.getAttribute('title') || ownText(el) || el.querySelector('img[alt]:not([alt=""])'));
  }
  function nameIconControls() {
    knownIcons.forEach(function (pair) {
      Array.prototype.forEach.call(document.querySelectorAll(pair[0]), function (el) { if (!el.getAttribute('aria-label')) el.setAttribute('aria-label', pair[1]); });
    });
    Array.prototype.forEach.call(document.querySelectorAll('button, a[href], [role="button"]'), function (el) {
      if (hasName(el)) return;
      var svg = el.querySelector('svg'); if (!svg) return;
      var cls = String(svg.getAttribute('class') || ''), name = null;
      Object.keys(iconWords).forEach(function (k) { if (!name && cls.indexOf(k) !== -1) name = iconWords[k]; });
      if (name) el.setAttribute('aria-label', name);
    });
  }
  nameIconControls();

  /* --- 13) Untitled frames (WCAG 4.1.2). Shopify's web-pixel sandbox frame and app frames without a
     title read as "frame" with no name. --- */
  function titleFrames() {
    Array.prototype.forEach.call(document.querySelectorAll('iframe:not([title])'), function (f) {
      var src = f.getAttribute('src') || '', host = '';
      try { host = new URL(src, location.href).hostname; } catch (e) {}
      var name = /web-pixel/.test(f.id + ' ' + src) ? 'Shopify analytics' : /preview-bar/.test(src) ? 'Shopify preview bar' : (host ? 'Embedded content from ' + host : 'Embedded content');
      f.setAttribute('title', name);
    });
  }
  titleFrames();

  /* --- 14) Sections outside landmarks (WCAG 1.3.1, best practice). The theme's header-group notices,
     announcement bar and footer promotions are siblings of <header>, <main> and <footer>, so their text
     is "outside any landmark". Expose each as a named region. --- */
  var landmarkSel = 'header, main, footer, nav, aside, [role="banner"], [role="main"], [role="contentinfo"], [role="navigation"], [role="complementary"], [role="region"], [role="dialog"], [role="search"]';
  Array.prototype.forEach.call(document.querySelectorAll('[id^="shopify-section"]'), function (sec) {
    if (sec.getAttribute('role') || sec.closest(landmarkSel)) return;
    if (sec.querySelector(landmarkSel)) return;
    if (!sec.textContent.replace(/\s+/g, '').length) return;
    var h = sec.querySelector('h1, h2, h3, h4, h5, h6');
    var label = (h && h.textContent.replace(/\s+/g, ' ').trim()) ? h.textContent.replace(/\s+/g, ' ').trim().slice(0, 60)
      : (/announcement/.test(sec.id) ? 'Announcement' : /promotion/.test(sec.id) ? 'Promotions' : 'Site notice');
    sec.setAttribute('role', 'region');
    sec.setAttribute('aria-label', label);
  });

  /* --- 15) Heading levels and redundant image text (best practice). Section titles the theme prints as
     <h3> directly under a page's <h1> ("You may also like") are announced at level 2; an image inside a
     link whose alt text repeats the link's own text is marked decorative so the name is not read twice. --- */
  if (document.querySelector('h1') && !document.querySelector('main h2')) {
    Array.prototype.forEach.call(document.querySelectorAll('main h3.section-header__title'), function (h) {
      if (!h.getAttribute('aria-level')) { h.setAttribute('role', 'heading'); h.setAttribute('aria-level', '2'); }
    });
  }
  Array.prototype.forEach.call(document.querySelectorAll('a[href] img[alt]'), function (img) {
    var a = img.closest('a'); if (!a) return;
    var alt = img.getAttribute('alt').replace(/\s+/g, ' ').trim().toLowerCase(); if (!alt) return;
    var clone = a.cloneNode(true);
    Array.prototype.forEach.call(clone.querySelectorAll('img, svg'), function (n) { n.parentNode && n.parentNode.removeChild(n); });
    var text = clone.textContent.replace(/\s+/g, ' ').trim().toLowerCase();
    if (text && text === alt) img.setAttribute('alt', '');
  });

  /* --- 16) Wishlist app modal (WCAG 1.3.2, 4.1.2). The HulkApps wishlist "Login required" dialog is hidden
     with opacity alone, so screen readers meet it in the reading order on every page. Hide it from
     assistive technology and out of the Tab order while it is closed; give it dialog semantics and a named close control when open. --- */
  function syncWishlistModal() {
    Array.prototype.forEach.call(document.querySelectorAll('.nm-modal-container'), function (c) {
      var cs = getComputedStyle(c);
      var open = cs.display !== 'none' && cs.visibility !== 'hidden' && parseFloat(cs.opacity) > 0.5;
      var focusables = c.querySelectorAll('a[href], button, input, select, textarea, [tabindex]');
      if (!open) {
        c.setAttribute('aria-hidden', 'true');
        Array.prototype.forEach.call(focusables, function (f) {
          if (f.getAttribute('tabindex') !== '-1') { f.setAttribute('data-a11y-tabindex', f.getAttribute('tabindex') === null ? '' : f.getAttribute('tabindex')); f.setAttribute('tabindex', '-1'); }
        });
        return;
      }
      c.removeAttribute('aria-hidden');
      Array.prototype.forEach.call(c.querySelectorAll('[data-a11y-tabindex]'), function (f) {
        var prev = f.getAttribute('data-a11y-tabindex');
        if (prev === '') f.removeAttribute('tabindex'); else f.setAttribute('tabindex', prev);
        f.removeAttribute('data-a11y-tabindex');
      });
      var m = c.querySelector('.nm-wishlist-login-modal') || c;
      if (!m.getAttribute('role')) {
        m.setAttribute('role', 'dialog');
        var h = m.querySelector('.nm-wishlist-login-modal-header span');
        m.setAttribute('aria-label', h && h.textContent.trim() ? h.textContent.trim() : 'Wishlist');
      }
      var btn = c.querySelector('.nm-modal-close-button');
      if (btn && !btn.getAttribute('aria-label')) { btn.setAttribute('role', 'button'); btn.setAttribute('tabindex', '0'); btn.setAttribute('aria-label', 'Close'); }
    });
  }
  syncWishlistModal();

  /* --- 17) Low-stock notice colour (WCAG 1.4.3). An app prints "Only N left" in rgb(238,68,46) on the
     page background (2.85:1). Darken it to #B3261E (about 6.5:1) until the app's own setting is changed. --- */
  function fixLowStock() {
    Array.prototype.forEach.call(document.querySelectorAll('.hengam-low-stock-description, .hengam-low-stock-description p'), function (el) {
      if (getComputedStyle(el).color === 'rgb(238, 68, 46)') el.style.setProperty('color', '#B3261E', 'important');
    });
  }
  fixLowStock();

  /* Re-apply 12, 13, 16 and 17 when the page changes (quick shop, cart re-render, app widgets loading) */
  var refreshTimer = null;
  function refreshAll() { nameIconControls(); titleFrames(); syncWishlistModal(); fixLowStock(); }
  if (window.MutationObserver) {
    new MutationObserver(function () { window.clearTimeout(refreshTimer); refreshTimer = window.setTimeout(refreshAll, 250); })
      .observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
  }
})();
